<template>
  <BaseArchive
    v-if="articles && meta"
    class="archive-author"
    :articles="articles"
    :link="link"
    :meta="meta"
    :style="cssVariables"
    slug="author"
  >
    <template #header>
      <AuthorHeader
        v-if="author"
        :author="author"
        :hide-additional="hideAdditionalDataAuthor"
      />
    </template>
  </BaseArchive>
</template>

<script lang="ts">
  import { ARCHIVES_ARTICLES_COUNT_ON_PAGE } from '~/constants';
  import BaseArchive from '~/components/BaseComponents/BaseArchive.vue';
  import AuthorHeader from '~/components/Archive/AuthorHeader.vue';
  import { getRouteParam } from '~/types/utils/route';
  import { useArchivesStore } from '~/store/archives';
  import { useSeoStore } from '~/store/seo';
  import { useHeadWithSeo } from '~/composables/useHeadWithSeo';

  export default defineNuxtComponent({
    name: 'ArchiveAuthor',

    components: {
      BaseArchive,
      AuthorHeader,
    },

    provide() {
      return {
        // для отправки аналитики
        eventName: () => 'Публичный профиль автора_выбор материала',
        eventItem: () => this.author?.name,
        eventCategory: () => 'Архив автора',
      };
    },

    scrollToTop: true,

    async setup() {
      const { $api } = useNuxtApp();
      const archiveStore = useArchivesStore();
      const seoStore = useSeoStore();

      const route = useRoute();
      const authorSlug = getRouteParam(route.params.authorSlug);
      const pageNumber = +getRouteParam(route.params.pageNumber || 1);
      const pagesDataRoute = route.fullPath.replace(route.hash, '');

      if (!authorSlug) {
        throw createError({ statusCode: 404, message: 'Invalid authorSlug' });
      }

      const { data, error } = await useAsyncData('author-posts', () =>
        $api.archives.author({
          slug: authorSlug,
          page: pageNumber,
          limit: ARCHIVES_ARTICLES_COUNT_ON_PAGE,
        }),
      );

      if (data.value?.data.meta.total_pages === 0) {
        throw createError({ statusCode: 404, message: 'No data' });
      } else if (error.value) {
        throw createError(error.value);
      }

      if (data.value?.data) {
        archiveStore.setMeta(data.value.data.meta);
        seoStore.addSeoByPath(pagesDataRoute, data.value.data.seo);
      }

      useHeadWithSeo();

      return {
        articles: data.value?.data.items,
        meta: data.value?.data.meta,
        author: data.value?.data.item,
        authorSlug,
        pageNumber,
      };
    },

    computed: {
      cssVariables(): Record<string, string> {
        return {
          '--content-bg': this.hideAdditionalDataAuthor ? '#000' : '#fff',
          '--author-border-radius': this.hideAdditionalDataAuthor ? '32px' : '0px',

          '--header-padding': this.hideAdditionalDataAuthor ? '16px' : '0px',
        };
      },

      hideAdditionalDataAuthor(): boolean {
        return !(this.pageNumber > 1);
      },

      link(): HTMLAnchorElement {
        return {
          href: `/author/${this.authorSlug}`,
        } as HTMLAnchorElement;
      },
    },
  });
</script>

<style lang="scss" scoped>
  :deep() {
    .author {
      border-radius: var(--author-border-radius);
    }
    .archive__header {
      background-color: var(--content-bg);
      border-radius: 32px;
      padding-bottom: var(--header-padding);
      @include tablet {
        background-color: #fff;
      }
    }
    .archive__container {
      background-color: var(--content-bg);
      padding-bottom: 0px;
      @include tablet {
        padding-bottom: 36px;
        background-color: #fff;
      }
    }
    .archive__content {
      border-radius: 32px;
      background-color: #fff;
    }
    .archive__pagination {
      padding: 12px 10px 24px;
      @include tablet {
        padding: 8px 16px;
      }
      @include big-tablet {
        padding: 8px 28px;
      }
      @include desktop {
        padding: 8px 32px;
      }
    }
  }
</style>
